import { FRESH_TIRE_HEALTH, TerrainType, } from "./types.js";
import { PHYSICS_CONFIG } from "./physicsConfig.js";
/** Shared rest threshold: contacts do not abrade a stationary vehicle. */
export const TIRE_MOTION_THRESHOLD = 0.2;
/**
 * Apply one fixed-tick worth of deterministic tire wear. The input health and
 * contacts are never mutated, which keeps render interpolation and replay
 * seeking from changing simulation state.
 */
export function applyTireWear(tires, wheelContacts, brakeTelemetry, config, stepMs) {
    return applyTireWearDetailed(tires, wheelContacts, brakeTelemetry, config, stepMs).tires;
}
export function applyTireWearDetailed(tires, wheelContacts, brakeTelemetry, config, stepMs) {
    const next = normalizeTireHealth(tires);
    const seconds = Number.isFinite(stepMs) ? Math.max(0, stepMs) / 1000 : 0;
    const zero = {
        grass: 0,
        gravel: 0,
        brake: 0,
        cornering: 0,
        total: 0,
    };
    if (seconds === 0)
        return { tires: next, telemetry: zero };
    const grassRate = finiteNonNegative(config.grassWearRate, PHYSICS_CONFIG.grassWearRate);
    const gravelRate = finiteNonNegative(config.gravelWearRate, PHYSICS_CONFIG.gravelWearRate);
    const brakeRate = finiteNonNegative(config.brakeWearRate, PHYSICS_CONFIG.brakeWearRate);
    const corneringRate = finiteNonNegative(config.corneringWearRate, PHYSICS_CONFIG.corneringWearRate);
    const maxSpeed = Math.max(finiteNonNegative(config.maxSpeed, 1), 0.001);
    const speed = finiteNonNegative(brakeTelemetry.speed, 0);
    const isMoving = speed > TIRE_MOTION_THRESHOLD;
    const speedFactor = isMoving ? clamp(speed / maxSpeed, 0, 1) : 0;
    const appliedBrake = clamp(brakeTelemetry.appliedBrake, 0, 1);
    const telemetry = { ...zero };
    for (const contact of wheelContacts) {
        let wear = 0;
        if (isMoving && contact.terrain === TerrainType.Grass) {
            const contribution = grassRate * seconds;
            wear += contribution;
            telemetry.grass += contribution;
        }
        if (isMoving && contact.terrain === TerrainType.Gravel) {
            const contribution = gravelRate * seconds;
            wear += contribution;
            telemetry.gravel += contribution;
        }
        if ((contact.wheel === "rearLeft" || contact.wheel === "rearRight") &&
            appliedBrake > 0 &&
            speedFactor > 0) {
            const contribution = brakeRate * seconds * appliedBrake * speedFactor;
            wear += contribution;
            telemetry.brake += contribution;
        }
        const utilization = clamp(brakeTelemetry.corneringUtilization?.[contact.wheel] ?? 0, 0, 1);
        if (isMoving && corneringRate > 0 && utilization > 0) {
            const contribution = corneringRate * seconds * utilization * speedFactor;
            wear += contribution;
            telemetry.cornering += contribution;
        }
        next[contact.wheel] = clamp(next[contact.wheel] - wear, 0, 100);
    }
    telemetry.total =
        telemetry.grass + telemetry.gravel + telemetry.brake + telemetry.cornering;
    return { tires: next, telemetry };
}
/** Continuous two-stage response shared by the tire model. */
export function tireGripMultiplier(percent, config) {
    const minimum = clamp(config.minimumLateralGrip ?? PHYSICS_CONFIG.minimumLateralGrip, 0.001, 1);
    const curve = Math.max(finiteNonNegative(config.wearCurve, PHYSICS_CONFIG.wearCurve), 0.01);
    const critical = clamp(finiteNonNegative(config.tireCriticalHealth, PHYSICS_CONFIG.tireCriticalHealth) / 100, 0.0001, 0.9999);
    const health = clamp(finiteNonNegative(percent, 0) / 100, 0, 1);
    const healthyAtCritical = minimum + (1 - minimum) * critical ** curve;
    if (health >= critical)
        return clamp(minimum + (1 - minimum) * health ** curve, minimum, 1);
    const criticalPenalty = clamp(finiteNonNegative(config.criticalGripPenalty, PHYSICS_CONFIG.criticalGripPenalty), 0, 4);
    const criticalCurve = Math.max(curve * (1 + criticalPenalty), 0.01);
    const ratio = health / critical;
    return clamp(minimum + (healthyAtCritical - minimum) * ratio ** criticalCurve, minimum, 1);
}
export function normalizeTireHealth(tires) {
    return {
        frontLeft: clamp(tires?.frontLeft ?? FRESH_TIRE_HEALTH.frontLeft, 0, 100),
        frontRight: clamp(tires?.frontRight ?? FRESH_TIRE_HEALTH.frontRight, 0, 100),
        rearLeft: clamp(tires?.rearLeft ?? FRESH_TIRE_HEALTH.rearLeft, 0, 100),
        rearRight: clamp(tires?.rearRight ?? FRESH_TIRE_HEALTH.rearRight, 0, 100),
    };
}
export function cloneTireHealth(tires) {
    return normalizeTireHealth(tires);
}
function clamp(value, min, max) {
    return Math.max(min, Math.min(max, Number.isFinite(value) ? value : min));
}
function finiteNonNegative(value, fallback) {
    return Number.isFinite(value) && value !== undefined
        ? Math.max(0, value)
        : fallback;
}
export const WHEEL_IDS = [
    "frontLeft",
    "frontRight",
    "rearLeft",
    "rearRight",
];
